import { sectionRenderer } from '@theme/section-renderer';

class BbdRebuySimilarProducts extends HTMLElement {
  /**
   * The observer for the rebuy recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, any>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the recommendations from Rebuy
   */
  async #loadRecommendations() {
    const { rebuyId, apiKey, productId, variantId, customerId, recommendationsPerformed } = this.dataset;

    if (!rebuyId || !apiKey || !productId) {
      this.#handleError(new Error('Rebuy Data-Source ID, API Key, and Product ID are required'));
      return;
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    const params = new URLSearchParams({
      key: apiKey,
      shopify_product_ids: productId,
      shopify_variant_ids: variantId || '',
      limit: '10'
    });

    if (customerId) {
      params.set('shopify_customer_id', customerId);
    }

    const url = `https://rebuyengine.com/api/v1/custom/id/${rebuyId}?${params}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      this.#renderRecommendations(cachedResponse);
      return;
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });

      if (!response.ok) {
        throw new Error(`Rebuy API error: ${response.status}`);
      }

      const data = await response.json();
      this.#cachedRecommendations[url] = data;
      this.#renderRecommendations(data);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#handleError(error);
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Render the recommendations data
   * @param {any} data
   */
  async #renderRecommendations(data) {
    const products = data.data || [];
    if (products.length === 0) {
      this.#handleError(new Error('No recommendations returned from Rebuy'));
      return;
    }

    const productIds = products.map((product) => product.id);
    const searchQuery = productIds.map((id) => `id:${id}`).join(' OR ');

    // We use the Section Renderer API with the predictive search endpoint
    // to fetch the section HTML with the product IDs
    const sectionId = this.dataset.sectionId;
    // eslint-disable-next-line
    const url = new URL(Theme.routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', searchQuery);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(Math.min(productIds.length, 10)));

    try {
      const htmlText = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');
      const newSection = doc.querySelector(`bbd-rebuy-similar-products[id="${this.id}"]`);

      if (newSection && newSection.innerHTML.trim().length) {
        this.innerHTML = newSection.innerHTML;
        this.dataset.recommendationsPerformed = 'true';
        this.dispatchEvent(new CustomEvent('datalayer:list-ready', { bubbles: true, detail: { container: this } }));
        // Clear the last-collection breadcrumb key when navigating from similar
        // products, as the product was not reached via a collection browse.
        this.addEventListener('click', (event) => {
          if (event.target.closest('a')) {
            localStorage.removeItem('bbd_last_collection');
          }
        });
      } else {
        this.#handleError(new Error('Failed to render recommendations HTML'));
      }
    } catch (error) {
      this.#handleError(error);
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('BBD Rebuy Similar Products error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading Rebuy recommendations';
  }
}

if (!customElements.get('bbd-rebuy-similar-products')) {
  customElements.define('bbd-rebuy-similar-products', BbdRebuySimilarProducts);
}
